import { useEffect, useState } from 'react';
import type { TenantConfig } from '@pink/shared';

import { verifyPinning, type PinningResult } from './verify-pinning.js';
import { PinningError } from './pinning-error.js';

/**
 * Status des Pinning-Bootstraps:
 * - `pending`: Verifikation läuft (initial und während StrictMode-Doppel-Mount).
 * - `ok`: Pinning erfolgreich, `verifiedKey` steht im Result.
 * - `error`: Pinning gescheitert, `reason` enthält den Sub-Code für Telemetrie.
 */
type PinningStatus = 'pending' | 'ok' | 'error';

interface UsePinningBootstrapState {
  readonly status: PinningStatus;
  readonly result: PinningResult | null;
}

interface UsePinningBootstrapOptions {
  readonly config: TenantConfig;
  /**
   * Pin-Fingerprint aus dem dekodierten Cancel-Token, Lowercase-Hex
   * (`SPKI_FINGERPRINT_LENGTH` Zeichen).
   */
  readonly expectedFingerprintHex: string;
  /**
   * Injektionspunkt für Tests, um die Pinning-Routine zu mocken.
   * Wenn nicht gesetzt: Default `verifyPinning` (Web-Crypto + Shared).
   */
  readonly verify?: (config: TenantConfig, fingerprintHex: string) => Promise<PinningResult>;
}

/**
 * Hook, der genau einmal pro `[config, expectedFingerprintHex]`-Identität die
 * Pinning-Verifikation auslöst und das Resultat im React-State spiegelt.
 *
 * StrictMode-Robustheit: Der Effekt benutzt ein `cancelled`-Flag, damit der
 * Doppel-Mount keinen zweiten `verifyPinning`-Aufruf produziert, der den
 * State überschreibt. WebCrypto-Calls bleiben ohnehin idempotent — die
 * Robustheit hier dient dem deterministischen Render-Verhalten und der
 * Telemetrie (kein Doppel-Counter).
 */
export function usePinningBootstrap(options: UsePinningBootstrapOptions): UsePinningBootstrapState {
  const { config, expectedFingerprintHex, verify } = options;
  const [state, setState] = useState<UsePinningBootstrapState>({
    status: 'pending',
    result: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'pending', result: null });
    const verifier = verify ?? verifyPinning;
    verifier(config, expectedFingerprintHex).then(
      (result) => {
        if (cancelled) {
          return;
        }
        setState({ status: result.ok ? 'ok' : 'error', result });
      },
      () => {
        if (cancelled) {
          return;
        }
        setState({
          status: 'error',
          result: { ok: false, reason: PinningError.KeyImportFailed },
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [config, expectedFingerprintHex, verify]);

  return state;
}
